import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Group, Badge, Tooltip, NumberInput, ActionIcon, TextInput } from '@mantine/core';
import { useRemoveBookingFromOrder, useUpdateServiceOrderBooking } from '@/hooks/api-hooks';
import { ServiceBooking, ServiceOrderBooking, ServiceOrder, ServiceOrderPaxGroup, ReservationPaxGroup, ServiceBookingPaxGroup, Business, PAX_CATEGORY_COLORS, PAX_CATEGORIES, PaxCategory } from '@turizi/shared';
import CardModal from '../card-modal';
import { notify } from '@/lib/notify';
import { IconBabyCarriage, IconCalendar, IconCheck, IconDeviceFloppy, IconDoorExit, IconExternalLink, IconSearch, IconUnlink, IconUserCheck, IconUserX, IconUsers, IconX } from '@tabler/icons-react';
import { EDIT_SERVICE_ORDER_PATH, RESERVATIONS_PATH } from '@/lib/navigation';
import { Link } from 'react-router-dom';
import { formatDayShort } from '@/lib/format-utils';
import { ServiceTitleStatic } from '../services/service-title';

export interface BookingAllocationPaxGroup {
    id?: number;
    pax_group_id: number;
    name?: string | null;
    custom_name?: string | null;
    adults: number;
    children: number;
    infants: number;
    seniors: number;
    guests: number;
    pax_count?: number;
    pax_group?: ReservationPaxGroup;
    custom_contact_phone?: string | null;
    coordinator?: Business | null;
}

export interface GroupAllocationCount {
    adults: number;
    children: number;
    infants: number;
    seniors: number;
    guests: number;
    notes: string;
}

export interface PaxGroupAllocationPayload {
    pax_group_id: number;
    adults: number;
    children: number;
    infants: number;
    seniors: number;
    guests: number;
    pax_count: number;
    notes: string | null;
}

interface EditBookingAllocationModalProps {
    opened: boolean;
    onClose: () => void;
    order: ServiceOrder;
    bookings?: ServiceBooking[];
    onSaved?: () => void;
    // Legacy single booking props for backward compatibility
    link?: ServiceOrderBooking;
    booking?: ServiceBooking;
    assignedPersonIds?: number[];
    assignedPaxGroups?: ServiceOrderPaxGroup[];
}

export default function EditBookingAllocationModal({
    opened,
    onClose,
    order,
    bookings,
    onSaved,
    link: legacyLink,
    booking: legacyBooking,
}: EditBookingAllocationModalProps): JSX.Element {
    const { t } = useTranslation();
    const updateMutation = useUpdateServiceOrderBooking();
    const removeBookingMutation = useRemoveBookingFromOrder();

    // Standard pax categories
    const ALL_PAX_CATEGORIES: PaxCategory[] = Object.values(PAX_CATEGORIES);


    // Resolve all bookings for this order
    const resolvedBookings = useMemo(() => {
        let list: ServiceBooking[] = [];
        if (bookings && bookings.length > 0) {
            list = [...bookings];
        } else if (legacyBooking) {
            list = [{
                ...legacyBooking,
                _service_order_booking: legacyLink || legacyBooking._service_order_booking,
                _original_booking: legacyBooking._original_booking || legacyBooking,
            }];
        } else {
            list = (order?.service_order_bookings?.map(link => {
                const b = link.service_booking;
                if (!b) return null;
                return {
                    ...b,
                    _service_order_booking: link,
                    _original_booking: b,
                };
            }).filter((b): b is NonNullable<typeof b> => Boolean(b))) || [];
        }

        return list.sort((a, b) => {
            const resDiff = (a.reservation_id ?? 0) - (b.reservation_id ?? 0);
            if (resDiff !== 0) return resDiff;
            return (a.id ?? 0) - (b.id ?? 0);
        });
    }, [bookings, order, legacyBooking, legacyLink]);

    // Map each booking to its pax groups (or synthetic fallback group)
    const bookingGroupsMap = useMemo(() => {
        const map: Record<number, BookingAllocationPaxGroup[]> = {};

        resolvedBookings.forEach(b => {
            const orig = b._original_booking || b;

            if (orig.pax_groups && orig.pax_groups.length > 0) {
                map[b.id] = orig.pax_groups.map((bg: ServiceBookingPaxGroup) => ({
                    id: bg.id,
                    pax_group_id: bg.pax_group_id || bg.id || 0,
                    custom_name: bg.custom_name,
                    adults: bg.adults ?? 0,
                    children: bg.children ?? 0,
                    infants: bg.infants ?? 0,
                    seniors: bg.seniors ?? 0,
                    guests: bg.guests ?? 0,
                    pax_count: bg.pax_count,
                    pax_group: bg.pax_group,
                    custom_contact_phone: bg.custom_contact_phone,
                    coordinator: bg.coordinator,
                }));
                return;
            }

            if (orig.reservation?.pax_groups && orig.reservation.pax_groups.length > 0) {
                map[b.id] = orig.reservation.pax_groups.map((rg: ReservationPaxGroup) => ({
                    id: rg.id,
                    pax_group_id: rg.id || 0,
                    custom_name: rg.name,
                    adults: rg.adults ?? 0,
                    children: rg.children ?? 0,
                    infants: rg.infants ?? 0,
                    seniors: rg.seniors ?? 0,
                    guests: rg.guests ?? 0,
                    pax_count: rg.pax_count,
                    pax_group: rg,
                    custom_contact_phone: rg.contact_phone,
                    coordinator: rg.coordinator,
                }));
                return;
            }

            // Fallback synthetic group for legacy bookings
            const adults = orig.adults ?? 0;
            const children = orig.children ?? 0;
            const infants = orig.infants ?? 0;
            const seniors = orig.seniors ?? 0;
            const guests = orig.guests ?? 0;
            const total = adults + children + seniors + guests + infants;

            map[b.id] = [{
                id: -b.id,
                pax_group_id: -b.id,
                name: null,
                custom_name: null,
                adults,
                children,
                infants,
                seniors,
                guests,
                pax_count: total,
                pax_group: orig.persons ? ({ persons: orig.persons } as any) : undefined,
                custom_contact_phone: null,
                coordinator: null,
            }];
        });

        return map;
    }, [resolvedBookings]);

    // Allocations state: groupId -> category counts
    const [groupAllocations, setGroupAllocations] = useState<Record<number, GroupAllocationCount>>({});
    const [initialGroupAllocations, setInitialGroupAllocations] = useState<Record<number, GroupAllocationCount>>({});

    // Track loading state per booking for saving/removing
    const [savingBookingIds, setSavingBookingIds] = useState<Record<number, boolean>>({});
    const [removingBookingIds, setRemovingBookingIds] = useState<Record<number, boolean>>({});
    const [isSavingAll, setIsSavingAll] = useState(false);

    // Search query filter
    const [searchQuery, setSearchQuery] = useState('');

    // Initialize allocations when modal is opened or order changes
    useEffect(() => {
        if (!opened) return;

        const initialMap: Record<number, GroupAllocationCount> = {};
        const orderPaxGroups = order.service_order_pax_groups || [];

        resolvedBookings.forEach(booking => {
            const groups = bookingGroupsMap[booking.id] || [];
            const bookingAssignedGroups = orderPaxGroups.filter(sog => sog.service_booking_id === booking.id);
            const hasExistingAssigned = bookingAssignedGroups.length > 0;
            const link = booking._service_order_booking || order.service_order_bookings?.find(sob => sob.service_booking_id === booking.id);

            groups.forEach(bg => {
                const gid = bg.pax_group_id;

                if (gid < 0) {
                    // Synthetic fallback group: use link counts or booking counts
                    initialMap[gid] = {
                        adults: link?.adults ?? bg.adults ?? 0,
                        children: link?.children ?? bg.children ?? 0,
                        infants: link?.infants ?? bg.infants ?? 0,
                        seniors: link?.seniors ?? bg.seniors ?? 0,
                        guests: link?.guests ?? bg.guests ?? 0,
                        notes: '',
                    };
                    return;
                }

                const existing = bookingAssignedGroups.find(sog => sog.pax_group_id === gid);
                if (existing) {
                    initialMap[gid] = {
                        adults: existing.adults ?? 0,
                        children: existing.children ?? 0,
                        infants: existing.infants ?? 0,
                        seniors: existing.seniors ?? 0,
                        guests: existing.guests ?? 0,
                        notes: existing.notes || '',
                    };
                } else if (!hasExistingAssigned) {
                    initialMap[gid] = {
                        adults: bg.adults ?? 0,
                        children: bg.children ?? 0,
                        infants: bg.infants ?? 0,
                        seniors: bg.seniors ?? 0,
                        guests: bg.guests ?? 0,
                        notes: '',
                    };
                } else {
                    initialMap[gid] = {
                        adults: 0,
                        children: 0,
                        infants: 0,
                        seniors: 0,
                        guests: 0,
                        notes: '',
                    };
                }
            });
        });

        setGroupAllocations(initialMap);
        setInitialGroupAllocations(initialMap);
    }, [opened, resolvedBookings, bookingGroupsMap, order]);

    // Check if a specific booking has changes
    const isBookingChanged = (bookingId: number): boolean => {
        const groups = bookingGroupsMap[bookingId] || [];
        return groups.some(g => {
            const curr = groupAllocations[g.pax_group_id];
            const init = initialGroupAllocations[g.pax_group_id];
            if (!curr || !init) return false;
            return (
                curr.adults !== init.adults ||
                curr.children !== init.children ||
                curr.infants !== init.infants ||
                curr.seniors !== init.seniors ||
                curr.guests !== init.guests ||
                (curr.notes || '') !== (init.notes || '')
            );
        });
    };

    // Check if any booking has changes
    const hasAnyChanges = useMemo(() => {
        return resolvedBookings.some(b => isBookingChanged(b.id));
    }, [resolvedBookings, groupAllocations, initialGroupAllocations, bookingGroupsMap]);

    // Filter bookings and groups based on search query
    const filteredBookingsData = useMemo(() => {
        const q = searchQuery.toLowerCase().trim();

        return resolvedBookings.map(booking => {
            const groups = bookingGroupsMap[booking.id] || [];
            const bookingMatch = (
                booking.id.toString().includes(q) ||
                (booking.reservation_id?.toString() || '').includes(q) ||
                (ServiceTitleStatic(booking.service_data) || '').toLowerCase().includes(q)
            );

            const visibleGroups = groups.filter(bg => {
                if (!q || bookingMatch) return true;
                const name = (bg.pax_group?.name || bg.custom_name || bg.name || '').toLowerCase();
                const paxNames = (bg.pax_group?.persons?.map(p => p.name).filter(Boolean) || []).join(' ').toLowerCase();
                const phone = (bg.pax_group?.contact_phone || bg.custom_contact_phone || '').toLowerCase();
                return name.includes(q) || paxNames.includes(q) || phone.includes(q);
            });

            return {
                booking,
                visibleGroups,
            };
        }).filter(item => item.visibleGroups.length > 0);
    }, [resolvedBookings, bookingGroupsMap, searchQuery]);

    // Column totals across all filtered groups
    const columnTotals = useMemo(() => {
        const catTotals: Record<string, { allocated: number; max: number }> = {
            adults: { allocated: 0, max: 0 },
            children: { allocated: 0, max: 0 },
            seniors: { allocated: 0, max: 0 },
            guests: { allocated: 0, max: 0 },
            infants: { allocated: 0, max: 0 },
        };

        let grandTotalAllocatedSeats = 0;
        let grandTotalGroupSeats = 0;
        let grandTotalAllocatedInfants = 0;
        let grandTotalGroupInfants = 0;

        filteredBookingsData.forEach(({ visibleGroups }) => {
            visibleGroups.forEach(bg => {
                const gid = bg.pax_group_id;
                const currentAlloc = groupAllocations[gid] || {
                    adults: 0,
                    children: 0,
                    infants: 0,
                    seniors: 0,
                    guests: 0,
                    notes: ''
                };

                const totalGroupSeats = (bg.adults || 0) + (bg.children || 0) + (bg.seniors || 0) + (bg.guests || 0);
                const totalGroupInfants = bg.infants || 0;
                const resolvedGroupSeats = totalGroupSeats > 0 ? totalGroupSeats : (bg.pax_count ?? 0);

                const allocatedSeats = (currentAlloc.adults || 0) + (currentAlloc.children || 0) + (currentAlloc.seniors || 0) + (currentAlloc.guests || 0);
                const allocatedInfants = currentAlloc.infants || 0;

                grandTotalAllocatedSeats += allocatedSeats;
                grandTotalGroupSeats += resolvedGroupSeats;
                grandTotalAllocatedInfants += allocatedInfants;
                grandTotalGroupInfants += totalGroupInfants;

                ALL_PAX_CATEGORIES.forEach(cat => {
                    const maxVal = bg[cat] !== undefined ? bg[cat] : (cat === 'adults' ? totalGroupSeats : 0);
                    catTotals[cat].allocated += (currentAlloc[cat] || 0);
                    catTotals[cat].max += (maxVal || 0);
                });
            });
        });

        return {
            catTotals,
            grandTotalAllocatedSeats,
            grandTotalGroupSeats,
            grandTotalAllocatedInfants,
            grandTotalGroupInfants,
        };
    }, [filteredBookingsData, groupAllocations]);

    // Quick helpers to assign / unassign a group
    // const assignGroupMax = (groupId: number, bg: BookingAllocationPaxGroup) => {
    //     setGroupAllocations(prev => ({
    //         ...prev,
    //         [groupId]: {
    //             adults: bg.adults ?? 0,
    //             children: bg.children ?? 0,
    //             infants: bg.infants ?? 0,
    //             seniors: bg.seniors ?? 0,
    //             guests: bg.guests ?? 0,
    //             notes: prev[groupId]?.notes || '',
    //         }
    //     }));
    // };

    // const unassignGroup = (groupId: number) => {
    //     setGroupAllocations(prev => ({
    //         ...prev,
    //         [groupId]: {
    //             adults: 0,
    //             children: 0,
    //             infants: 0,
    //             seniors: 0,
    //             guests: 0,
    //             notes: prev[groupId]?.notes || '',
    //         }
    //     }));
    // };

    // Batch actions across all bookings
    const handleAssignAll = () => {
        const next: Record<number, GroupAllocationCount> = { ...groupAllocations };
        resolvedBookings.forEach(booking => {
            const groups = bookingGroupsMap[booking.id] || [];
            groups.forEach(bg => {
                next[bg.pax_group_id] = {
                    adults: bg.adults ?? 0,
                    children: bg.children ?? 0,
                    infants: bg.infants ?? 0,
                    seniors: bg.seniors ?? 0,
                    guests: bg.guests ?? 0,
                    notes: groupAllocations[bg.pax_group_id]?.notes || '',
                };
            });
        });
        setGroupAllocations(next);
    };

    const handleUnassignAll = () => {
        const next: Record<number, GroupAllocationCount> = { ...groupAllocations };
        resolvedBookings.forEach(booking => {
            const groups = bookingGroupsMap[booking.id] || [];
            groups.forEach(bg => {
                next[bg.pax_group_id] = {
                    adults: 0,
                    children: 0,
                    infants: 0,
                    seniors: 0,
                    guests: 0,
                    notes: groupAllocations[bg.pax_group_id]?.notes || '',
                };
            });
        });
        setGroupAllocations(next);
    };

    // Save allocation for a single booking
    const saveBooking = async (booking: ServiceBooking) => {
        const link = booking._service_order_booking || order.service_order_bookings?.find(sob => sob.service_booking_id === booking.id);
        if (!link) {
            notify.error(t('error'), t('booking_allocation_update_failed'));
            return;
        }

        setSavingBookingIds(prev => ({ ...prev, [booking.id]: true }));

        const groups = bookingGroupsMap[booking.id] || [];
        let finalAdults = 0;
        let finalChildren = 0;
        let finalInfants = 0;
        let finalSeniors = 0;
        let finalGuests = 0;
        let paxGroupsPayload: PaxGroupAllocationPayload[] | undefined = undefined;

        const hasRealPaxGroups = groups.some(g => g.pax_group_id > 0);

        if (hasRealPaxGroups) {
            paxGroupsPayload = groups.filter(g => g.pax_group_id > 0).map(bg => {
                const counts = groupAllocations[bg.pax_group_id] || {
                    adults: 0,
                    children: 0,
                    infants: 0,
                    seniors: 0,
                    guests: 0,
                    notes: ''
                };
                finalAdults += counts.adults;
                finalChildren += counts.children;
                finalInfants += counts.infants;
                finalSeniors += counts.seniors;
                finalGuests += counts.guests;
                return {
                    pax_group_id: bg.pax_group_id,
                    adults: counts.adults,
                    children: counts.children,
                    infants: counts.infants,
                    seniors: counts.seniors,
                    guests: counts.guests,
                    pax_count: counts.adults + counts.children + counts.seniors + counts.guests,
                    notes: counts.notes || null,
                };
            });
        } else {
            const counts = groupAllocations[-booking.id] || {
                adults: 0,
                children: 0,
                infants: 0,
                seniors: 0,
                guests: 0,
                notes: ''
            };
            finalAdults = counts.adults;
            finalChildren = counts.children;
            finalInfants = counts.infants;
            finalSeniors = counts.seniors;
            finalGuests = counts.guests;
        }

        try {
            await updateMutation.mutateAsync({
                id: link.id,
                data: {
                    adults: finalAdults,
                    children: finalChildren,
                    infants: finalInfants,
                    seniors: finalSeniors,
                    guests: finalGuests,
                    pax_groups: paxGroupsPayload,
                }
            });

            // Update initial state for this booking's groups
            setInitialGroupAllocations(prev => {
                const next = { ...prev };
                groups.forEach(bg => {
                    if (groupAllocations[bg.pax_group_id]) {
                        next[bg.pax_group_id] = { ...groupAllocations[bg.pax_group_id] };
                    }
                });
                return next;
            });

            notify.success(t('success'), t('booking_allocation_updated'));
            onSaved?.();
        } catch (error) {
            console.error('Failed to update booking allocation:', error);
            notify.error(t('error'), t('booking_allocation_update_failed'));
        } finally {
            setSavingBookingIds(prev => ({ ...prev, [booking.id]: false }));
        }
    };

    // Save all bookings that have changes
    const handleSaveAll = async () => {
        const changedBookings = resolvedBookings.filter(b => isBookingChanged(b.id));
        if (changedBookings.length === 0) return;

        setIsSavingAll(true);
        try {
            await Promise.all(changedBookings.map(b => saveBooking(b)));
        } finally {
            setIsSavingAll(false);
        }
    };

    // Unassign a booking from the order
    const removeBooking = async (bookingId: number) => {
        if (!order?.id) return;
        setRemovingBookingIds(prev => ({ ...prev, [bookingId]: true }));

        try {
            await removeBookingMutation.mutateAsync({
                serviceOrderId: order.id,
                serviceBookingId: bookingId
            });
            notify.success(t('success'), t('booking_removed_from_order_successfully'));
            onSaved?.();
            if (resolvedBookings.length <= 1) {
                onClose();
            }
        } catch (error) {
            console.error('Failed to remove booking from order:', error);
            notify.error(t('error'), t('booking_removed_from_order_failed'));
        } finally {
            setRemovingBookingIds(prev => ({ ...prev, [bookingId]: false }));
        }
    };

    return (
        <CardModal
            size="full"
            opened={opened}
            onClose={onClose}
            header={
                <div className="w-full">
                    <div className="flex justify-between items-start">
                        <div className="flex gap-4 items-start flex-1">
                            <div className="flex-1 space-y-1 mr-10">
                                <div className="flex flex-wrap items-center gap-2">
                                    <Link
                                        to={EDIT_SERVICE_ORDER_PATH.replace(':id', order.id?.toString() || '')}
                                        target="_blank"
                                        className="text-lg font-bold text-slate-800 hover:text-blue-600 transition-colors flex items-center gap-1.5"
                                    >
                                        <span> #{order.id}</span>
                                        <IconExternalLink size={14} className="text-slate-400" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            }
            footer={
                filteredBookingsData.length > 0 ? (
                    <div className="flex items-center justify-around gap-4 w-full flex-wrap select-none">
                        <div className="flex items-center gap-2.5">
                            <div className='text-xxs text-gray-500'>
                                {filteredBookingsData.length} {t('reservations')}
                            </div>
                        </div>

                        {/* Center: Categories breakdown with letter badges */}
                        <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
                            {ALL_PAX_CATEGORIES.map((cat) => {
                                const { allocated, max } = columnTotals.catTotals[cat] || { allocated: 0, max: 0 };
                                const colors = PAX_CATEGORY_COLORS[cat] || { bg: 'bg-slate-100', text: 'text-slate-700' };
                                const initial = t(cat).charAt(0).toUpperCase();

                                return (
                                    <Tooltip key={cat} label={t(cat)} withArrow openDelay={150}>
                                        <div className="flex items-center gap-1.5 cursor-default">
                                            <span className={`w-5 h-5 rounded-full ${colors.bg} ${colors.text} inline-flex items-center justify-center text-xs font-bold shrink-0`}>
                                                {initial}
                                            </span>
                                            <div className="flex items-baseline gap-0.5">
                                                <span className="font-bold text-sm text-slate-800">
                                                    {allocated}
                                                </span>
                                                <span className="text-xs text-slate-400 font-medium">
                                                    /{max}
                                                </span>
                                            </div>
                                        </div>
                                    </Tooltip>
                                );
                            })}
                        </div>

                        {/* Right: Grand total badge */}
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1 text-sm solid-box-border-dark">
                                <IconUsers size={14} color='gray' />
                                <div className='font-bold'>{columnTotals.grandTotalAllocatedSeats}</div>
                                <div className='font-light'>/ {columnTotals.grandTotalGroupSeats}</div>
                            </div>
                            {(columnTotals.grandTotalGroupInfants > 0 || columnTotals.grandTotalAllocatedInfants > 0) && (
                                <div className="flex items-center gap-1 text-sm solid-box-border-dark" title={t('infants')}>
                                    <IconBabyCarriage size={14} color='gray' />
                                    <div className='font-bold'>{columnTotals.grandTotalAllocatedInfants}</div>
                                    <div className='font-light'>/ {columnTotals.grandTotalGroupInfants}</div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : undefined
            }
        >
            <div className="space-y-4">
                {/* Control Bar: Search, Stats, and Quick Actions */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                    <div className="w-full sm:w-80">
                        <TextInput
                            size="xs"
                            placeholder={t('search_allocations_placeholder')}
                            leftSection={<IconSearch size={14} className="text-slate-400" />}
                            rightSection={
                                searchQuery ? (
                                    <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => setSearchQuery('')}>
                                        <IconX size={12} />
                                    </ActionIcon>
                                ) : null
                            }
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.currentTarget.value)}
                        />
                    </div>

                    <div className="flex items-center gap-2 self-end sm:self-auto flex-wrap">
                        <div className="flex items-center gap-1">
                            <Tooltip label={t('assign_all_groups')} withArrow openDelay={200}>
                                <ActionIcon
                                    size="sm"
                                    variant="light"
                                    color="teal"
                                    onClick={handleAssignAll}
                                >
                                    <IconUserCheck size={14} />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label={t('unassign_all_groups')} withArrow openDelay={200}>
                                <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    color="gray"
                                    onClick={handleUnassignAll}
                                >
                                    <IconUserX size={14} />
                                </ActionIcon>
                            </Tooltip>
                        </div>

                        {hasAnyChanges && (
                            <Button
                                size="xs"
                                color="black"
                                loading={isSavingAll}
                                onClick={handleSaveAll}
                                leftSection={<IconDeviceFloppy size={14} />}
                                className="ml-1"
                            >
                                {t('save_all_changes')}
                            </Button>
                        )}
                    </div>
                </div>

                {/* Table Container */}
                <div className="border border-slate-200 rounded-lg overflow-hidden bg-white shadow-xs">
                    <div className="max-h-[68vh] overflow-y-auto">
                        <table className="w-full text-left text-xs border-collapse">
                            <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-10 text-[11px] font-bold text-slate-600 uppercase tracking-wider select-none shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
                                <tr>
                                    <th className="py-2.5 px-3 min-w-[220px]">
                                    </th>
                                    {ALL_PAX_CATEGORIES.map(cat => {
                                        const colors = PAX_CATEGORY_COLORS[cat] || { text: 'text-slate-700' };
                                        return (
                                            <th key={cat} className={`py-2.5 px-2 text-center w-20 ${colors.text}`}>
                                                {t(cat)}
                                            </th>
                                        );
                                    })}
                                    <th className="py-2.5 px-2 text-center w-24">
                                    </th>
                                    <th className="py-2.5 px-3 text-right w-28">

                                    </th>
                                </tr>
                            </thead>

                            {filteredBookingsData.length === 0 ? (
                                <tbody>
                                    <tr>
                                        <td
                                            colSpan={ALL_PAX_CATEGORIES.length + 3}
                                            className="py-12 text-center text-xs text-slate-400"
                                        >
                                            {t('no_data')}
                                        </td>
                                    </tr>
                                </tbody>
                            ) : (
                                filteredBookingsData.map(({ booking, visibleGroups }) => {
                                    const bookingHasChanges = isBookingChanged(booking.id);
                                    const isSavingThisBooking = Boolean(savingBookingIds[booking.id]);
                                    const isRemovingThisBooking = Boolean(removingBookingIds[booking.id]);

                                    const bookingGroups = bookingGroupsMap[booking.id] || [];
                                    let bookingTotalAllocatedSeats = 0;
                                    let bookingTotalSeats = 0;
                                    bookingGroups.forEach(bg => {
                                        const gid = bg.pax_group_id;
                                        const currentAlloc = groupAllocations[gid] || { adults: 0, children: 0, infants: 0, seniors: 0, guests: 0, notes: '' };
                                        const allocatedSeats = (currentAlloc.adults || 0) + (currentAlloc.children || 0) + (currentAlloc.seniors || 0) + (currentAlloc.guests || 0) + (currentAlloc.infants || 0);
                                        const maxSeats = (bg.adults || 0) + (bg.children || 0) + (bg.seniors || 0) + (bg.guests || 0) + (bg.infants || 0);
                                        bookingTotalAllocatedSeats += allocatedSeats;
                                        bookingTotalSeats += (maxSeats > 0 ? maxSeats : (bg.pax_count ?? 0));
                                    });

                                    return (
                                        <tbody
                                            key={booking.id}
                                            className="divide-y divide-slate-100 border-b-1 border-slate-50"
                                        >
                                            {/* Divider Row: Reservation & Service Title */}
                                            <tr className="bg-slate-50 border-t-1 border-b border-slate-100 select-none">
                                                <td colSpan={ALL_PAX_CATEGORIES.length + 3} className="py-2 px-3">
                                                    <div className="flex items-center gap-3">
                                                        {/* Left: Reservation Badge, Booking ID, Service Title, Category */}
                                                        <div className="flex items-center gap-2.5 flex-wrap min-w-0 text-xxs">
                                                            <Link
                                                                to={`${RESERVATIONS_PATH}/${booking.reservation_id}`}
                                                                target="_blank"
                                                                className="flex items-center gap-0.5 font-bold"
                                                            >
                                                                <IconCalendar size={13} />
                                                                <span>#{booking.reservation_id}</span>
                                                            </Link>
                                                            <span>
                                                                #{booking.id}
                                                            </span>
                                                            <span className="text-xxs text-slate-500 font-medium italic">
                                                                {ServiceTitleStatic(booking.service_data)}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-4 text-xxs">
                                                            <Tooltip label={t('unassign_to_order')} withArrow openDelay={200}>
                                                                <ActionIcon
                                                                    size="xs"
                                                                    variant="light"
                                                                    color="#888"
                                                                    loading={isRemovingThisBooking}
                                                                    onClick={() => removeBooking(booking.id)}
                                                                >
                                                                    <IconDoorExit size={15} />
                                                                </ActionIcon>
                                                            </Tooltip>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>

                                            {/* Groups Rows */}
                                            {visibleGroups.map((bg) => {
                                                const gid = bg.pax_group_id;
                                                const currentAlloc = groupAllocations[gid] || {
                                                    adults: 0,
                                                    children: 0,
                                                    infants: 0,
                                                    seniors: 0,
                                                    guests: 0,
                                                    notes: ''
                                                };

                                                const groupName = bg.pax_group?.name || bg.custom_name || bg.name || t('general_pax');
                                                const totalGroupSeats = (bg.adults || 0) + (bg.children || 0) + (bg.seniors || 0) + (bg.guests || 0);
                                                const totalGroupInfants = bg.infants || 0;
                                                const totalInGroup = (totalGroupSeats + totalGroupInfants) > 0 ? (totalGroupSeats + totalGroupInfants) : (bg.pax_count ?? 0);

                                                const paxNames: string[] = bg.pax_group?.persons?.map(p => p.name).filter((n): n is string => Boolean(n)) || [];

                                                const allocatedSeats = (currentAlloc.adults || 0) + (currentAlloc.children || 0) + (currentAlloc.seniors || 0) + (currentAlloc.guests || 0);
                                                const allocatedInfants = currentAlloc.infants || 0;
                                                const totalAllocated = allocatedSeats + allocatedInfants;

                                                const isFullyAssigned = totalInGroup > 0 && totalAllocated === totalInGroup;
                                                const isOverAssigned = totalAllocated > totalInGroup;
                                                const isPartiallyAssigned = totalAllocated > 0 && !isFullyAssigned && !isOverAssigned;
                                                const isUnassigned = totalAllocated === 0;

                                                return (
                                                    <tr
                                                        key={gid}
                                                        className={`hover:bg-slate-50/70 transition-colors ${isFullyAssigned
                                                            ? 'bg-teal-50/15'
                                                            : isPartiallyAssigned
                                                                ? 'bg-blue-50/15'
                                                                : ''
                                                            }`}
                                                    >
                                                        {/* Column 1: Pax Group Details */}
                                                        <td className="py-2.5 px-3 align-middle">
                                                            <div className="flex items-center gap-2 min-w-0">
                                                                <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 text-xs font-bold ${isFullyAssigned
                                                                    ? 'bg-teal-100 text-teal-700'
                                                                    : isPartiallyAssigned
                                                                        ? 'bg-blue-100 text-blue-700'
                                                                        : 'bg-slate-100 text-slate-600'
                                                                    }`}>
                                                                    <IconUsers size={13} />
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <span
                                                                        className="font-semibold text-xs text-slate-800 truncate block max-w-[220px] sm:max-w-xs"
                                                                        title={groupName}
                                                                    >
                                                                        {groupName}
                                                                    </span>
                                                                    {paxNames.length > 0 && (
                                                                        <div className="text-[10px] text-slate-400 truncate max-w-[220px] sm:max-w-xs">
                                                                            {paxNames.join(', ')}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>

                                                        {/* Columns 2-6: Categories Inputs */}
                                                        {ALL_PAX_CATEGORIES.map((cat) => {
                                                            const maxVal = bg[cat] !== undefined ? bg[cat] : (cat === 'adults' ? totalGroupSeats : 0);
                                                            if (maxVal === 0) {
                                                                return (
                                                                    <td key={cat} className="py-2 px-2 text-center text-slate-300 font-medium select-none align-middle">
                                                                        -
                                                                    </td>
                                                                );
                                                            }

                                                            return (
                                                                <td key={cat} className="py-2 px-2 text-center align-middle">
                                                                    <div className="inline-flex items-center gap-1 justify-center">
                                                                        <NumberInput
                                                                            hideControls
                                                                            value={currentAlloc[cat]}
                                                                            onChange={(val) => {
                                                                                const num = typeof val === 'number' ? val : (parseInt(val as string) || 0);
                                                                                const clamped = Math.max(0, Math.min(maxVal, num));
                                                                                setGroupAllocations(prev => ({
                                                                                    ...prev,
                                                                                    [gid]: { ...prev[gid], [cat]: clamped }
                                                                                }));
                                                                            }}
                                                                            min={0}
                                                                            max={maxVal}
                                                                            size="xs"
                                                                            step={1}
                                                                            styles={{
                                                                                input: {
                                                                                    width: 48,
                                                                                    height: 26,
                                                                                    minHeight: 26,
                                                                                    fontSize: 11,
                                                                                    fontWeight: 600,
                                                                                    textAlign: 'center',
                                                                                    padding: '0 4px',
                                                                                }
                                                                            }}
                                                                        />
                                                                        <span className="text-[10px] text-slate-400 font-medium select-none">
                                                                            /{maxVal}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                            );
                                                        })}

                                                        {/* Column 7: Allocated Badge */}
                                                        <td className="py-2 px-2 text-center whitespace-nowrap align-middle">
                                                            <div className={`
                                                                text-xxs
                                                                ${isOverAssigned && 'text-red-500'}
                                                                ${(isUnassigned || isPartiallyAssigned) && 'text-green-500'}
                                                                ${isFullyAssigned && 'text-gray-400'}
                                                            `}>
                                                                <span className='font-bold'>{totalAllocated}</span> / {totalInGroup}
                                                            </div>
                                                        </td>

                                                        {/* Column 8: Actions (Group Quick Assign/Unassign + Save Icon) */}
                                                        <td className="py-2.5 px-3 align-middle text-right">
                                                            <div className="flex items-center justify-end gap-4">
                                                                {/* <Tooltip label={t('assign_all_group')} withArrow openDelay={200}>
                                                                    <ActionIcon
                                                                        size="xs"
                                                                        variant='light'
                                                                        color="teal"
                                                                        disabled={isFullyAssigned}
                                                                        onClick={() => assignGroupMax(gid, bg)}
                                                                    >
                                                                        <IconCheck size={12} stroke={2.5} />
                                                                    </ActionIcon>
                                                                </Tooltip>

                                                                <Tooltip label={t('unassign_group')} withArrow openDelay={200}>
                                                                    <ActionIcon
                                                                        size="xs"
                                                                        variant="light"
                                                                        color="pink"
                                                                        disabled={isUnassigned}
                                                                        onClick={() => unassignGroup(gid)}
                                                                    >
                                                                        <IconX size={12} stroke={2.5} />
                                                                    </ActionIcon>
                                                                </Tooltip> */}
                                                                {/* 
                                                                <Tooltip label={bookingHasChanges ? t('save') : t('saved')} withArrow openDelay={200}>
                                                                    <ActionIcon
                                                                        size="xs"
                                                                        variant={bookingHasChanges ? "filled" : "subtle"}
                                                                        color={bookingHasChanges ? "black" : "gray"}
                                                                        disabled={!bookingHasChanges || isSavingThisBooking}
                                                                        loading={isSavingThisBooking}
                                                                        onClick={() => saveBooking(booking)}
                                                                        className="ml-0.5"
                                                                    >
                                                                        <IconDeviceFloppy size={13} />
                                                                    </ActionIcon>
                                                                </Tooltip> */}
                                                                <Button
                                                                    title={t('save')}
                                                                    size="xs"
                                                                    variant="subtle"
                                                                    color='black'
                                                                    disabled={!bookingHasChanges || isSavingThisBooking}
                                                                    loading={isSavingThisBooking}
                                                                    onClick={() => saveBooking(booking)}
                                                                >
                                                                    <IconDeviceFloppy size={13} />
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    );
                                })
                            )}
                        </table>
                    </div>
                </div>
            </div>
        </CardModal>
    );
}
